import PropTypes from 'prop-types';
import React, {
  useEffect,
  useState,
} from 'react';

import { Map, Iterable } from 'immutable';

import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';

import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { alpha } from "@mui/material";

import Display from 'components/Generic/Display';
import directUpload from 'helpers/direct_upload';

import {
  PROCESSING_STATUS,
  UPLOAD_ERROR_STATUS,
  RECOGNITION_ERROR_STATUS,
  UPLOADED_STATUS,
} from './constants'

const STAUS_ICONS = {
  recognized: <CheckCircleIcon color="primary" />,
  unrecognized: <ErrorIcon />,
  partially_recognized: <ErrorIcon />,
  upload_error: <ErrorIcon />,
  recognition_error: <ErrorIcon />,
  failed: <ErrorIcon />,
  processing: null,
  uploaded: <CheckCircleIcon color="primary" />,
};

function File({ object, onUpload, removeFile, documentName, country, disabled, recognize }) {

  const theme = useTheme();

  const [state, setState] = useState(new Map());
  const file = state.get('file', null);
  const status = state.get('status', object.get('status'));

  const [response, error, finished, progress] = directUpload(file, recognize ? 'response_recognize' : 'response', documentName, country);

  useEffect(() => {
    if (object === null || !object.get('blob') || object.get('signedBlobId')) return;

    setState(state.set('file', object.get('blob')));
  }, [object]);

  useEffect(() => {
    if (!finished) return;

    let status = response.recognition && response.recognition.result || UPLOADED_STATUS;
    if (error && error.recognition) status = RECOGNITION_ERROR_STATUS;
    if (error && error.upload) status = UPLOAD_ERROR_STATUS;

    const newObject = object.set('signedBlobId', response.signedBlobId)
      .set('status', status)
      .set('error', error);

    setState(state => state.set('status', status));

    onUpload(newObject);
  }, [response, error, finished, progress]);

  const onRemoveFile = () => {
    removeFile(object.get('id'));
  }

  function Progress({ progress }) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <Box sx={{ width: '100%', mr: 1 }}>
          <LinearProgress variant="determinate" value={progress} />
        </Box>
        <Box sx={{ minWidth: 35 }}>
          <Typography variant="body2" color="text.secondary">{`${progress}%`}</Typography>
        </Box>
      </Box>
    );
  }

  function Status ({ status }) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        {STAUS_ICONS[status]}
        <Typography variant="p" component="p" sx={{ pl: 1 }}>{t(`eos.landing_page.text.upload.status.${status}`)}</Typography>
      </Box>
    );
  }

  return (
    // TODO: Definitely a refactor to integrate Mobile/Desktop with the same components
    <>
      <Box sx={{ display: { xs: 'none', sm: 'block', md: 'block', lg: 'block' } }}>
        <Grid container columns={16} spacing={0} data-testid="file" sx={{ width: '100%', px: 3, py: 2, backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
          <Display if={status === PROCESSING_STATUS}>
            <Grid item sm={8} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography data-testid="file-upload-file-name" variant="p" sx={{ textOverflow: "ellipsis", overflow: "hidden" }}>{object.get('blob').name}</Typography>
            </Grid>

            <Grid item sm={4} sx={{ display: 'flex', alignItems: 'center' }} data-testid="fileupload-file-progress">
              <Progress progress={progress} />
            </Grid>

            <Grid item sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <Status status={status} />
            </Grid>
          </Display>

          <Display if={status !== PROCESSING_STATUS}>
            <Grid item sm={11} sx={{ display: 'flex', alignItems: 'center', textOverflow: "ellipsis", overflow: "hidden" }}>
              <Typography variant="p" sx={{ textOverflow: "ellipsis", overflow: "hidden" }}>{object.get('blob').name}</Typography>
            </Grid>

            <Grid item sm={4} sx={{ display: 'flex', alignItems: 'center' }} data-testid="fileupload-file-status">
              <Status status={status} />
            </Grid>
          </Display>

          <Display if={!disabled}>
            <Grid item sm={1} sx={{ display: 'flex', flexDirection: 'row-reverse' }}>
              <CloseIcon data-testid="fileupload-file-remove" sx={{ alignSelf: 'end', display: 'flex', direction: 'row-reverse', cursor: 'pointer' }} onClick={onRemoveFile} color="primary" />
            </Grid>
          </Display>
        </Grid>
      </Box>

      <Box sx={{ display: { xs: 'block', sm: 'none', md: 'none', lg: 'none' } }}>
        <Grid container columns={16} spacing={0} sx={{ width: '100%', px: 3, py: 2, backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
          <Display if={status === PROCESSING_STATUS}>
            <Grid item xs={15} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="p" sx={{ textOverflow: "ellipsis", overflow: "hidden" }}>{object.get('blob').name}</Typography>
            </Grid>

            <Display if={!disabled}>
              <Grid item xs={1} sx={{ display: 'flex', flexDirection: 'row-reverse', p: 0, cursor: 'pointer' }}>
                <CloseIcon sx={{ cursor: 'pointer' }} onClick={onRemoveFile} color="primary" />
              </Grid>
            </Display>

            <Grid item xs={10} sx={{ display: 'flex', alignItems: 'center', pt: 2 }}>
              <Progress progress={progress} />
            </Grid>

            <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', pt: 2 }}>
              <Status status={status} />
            </Grid>
          </Display>

          <Display if={status !== PROCESSING_STATUS}>
            <Grid item xs={15} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="p" sx={{ textOverflow: "ellipsis", overflow: "hidden" }}>{object.get('blob').name}</Typography>
            </Grid>

            <Display if={!disabled}>
              <Grid item xs={1} sx={{ display: 'flex', flexDirection: 'row-reverse', p: 0 }}>
                <CloseIcon sx={{ cursor: 'pointer' }} onClick={onRemoveFile} color="primary" />
              </Grid>
            </Display>

            <Grid item xs={16} sx={{ display: 'flex', alignItems: 'center', pt: 2 }}>
              <Status status={status} />
            </Grid>
          </Display>

        </Grid>
      </Box>
    </>
  )
}

File.defaultProps = {
  object: null,
  onUpload: null,
  removeFile: null,
  documentName: null,
  country: null,
  disabled: null,
  recognize: null,
};

File.propTypes = {
  object: PropTypes.objectOf(Iterable),
  onUpload: PropTypes.func,
  removeFile: PropTypes.func,
  documentName: PropTypes.string,
  country: PropTypes.string,
  disabled: PropTypes.bool,
  recognize: PropTypes.bool,
};

export default File;
